/* eslint-disable @typescript-eslint/no-explicit-any */
import Core from '../common/Core';
import { coreDependencies } from './dependencies';
import { cookie, findTopDomain } from '@turnercode/cdp-cookie';
import { v4 as uuid } from 'uuid';
import { FeatureFlagClient } from '@turnercode/cdp-feature-flag-client';
import Queue from '@segment/localstorage-retry';
import {
  PsmConfig,
  SessionProperties,
  NavigationProperties,
  Device,
  Ids,
  IPsm,
  UserConsentEventDetails,
  COPPAPrivacyModesEnum,
} from '@turnercode/types';
import { payloadCore } from './payloadCore';
import {
  CCPA_LOCATIONS,
  URLS,
  MAX_SESSION_DURATION,
  featureFlagDefaults,
  queueOptions,
  EMEA_LOCATIONS,
  LATAM_LOCATIONS,
} from '../utils/constants';
import { NavigationTracker } from './navigationTracker';
import { processQueue } from '../utils/processQueue';
import { sendRequest } from '../utils/sendRequest';
import { psmConstructorProps } from '../common/psm';
import { checkOutsideUS, getTCFSnapshot, unwatchTCFConsent, watchTCFConsent } from './privacy';
import { watchCnnUid } from './cnnUid';
import { createLogger, warn, info, debug } from '../utils/logger';
import { getReferrer, setBrowser } from '../utils/browser';
import { Library, PayloadCore } from '../interfaces';
import {
  getAttUuid,
  getCnnUid,
  getConvivaId,
  getEcid,
  getKruxId,
  getLiveRampAtsId,
  getZionId,
  hydratePayload,
} from '../utils/hydratePayload';
import { PSM_SDK_WEB_VERSION } from '../../../prism-module-telemetry/src/common/constants';
import {
  buildClientBlock,
  buildPermutiveEvent,
  MOSAIC_EVENT_TYPE,
  MosaicEventEnvelope,
  PermutiveClient,
  PermutiveEventProperties,
} from './mosaic/permutiveEvent';
import { getBoltUserId, getDeviceId, getViewId, newViewId, ResolvedIdentity, resolveIdentity } from './mosaic/identity';
import { isMosaicTestOverrideEnabled, logMosaic } from './mosaic/log';
import {
  buildGeoInfo,
  buildPageviewProperties,
  getArticleFromDocument,
  PermutiveArticle,
  PermutiveGeoInfo,
} from './mosaic/pageview';
import { isMosaicEventEnabled, isMosaicPageEnabled } from './mosaic/gating';
import { buildConsentBlock, PermutiveConsent } from './mosaic/consent';
import { buildLinkClickProperties, resolveLinkClickDest } from './mosaic/linkClick';
import { installMosaicSpaPageviews } from './mosaic/spaPageview';
import {
  buildSlotProperties,
  installGptSlotTracking,
  SLOT_CLICKED_EVENT,
  SLOT_VIEWABLE_EVENT,
  SlotAttribution,
} from './mosaic/slots';
import { MosaicConfig } from '@turnercode/types';

/**
 * Window flag marking that a PRISM bundle already owns Mosaic on this page.
 * Deliberately separate from `window.psmInitialized`, which is only set on the
 * consented path and so is never armed where Mosaic needs it most (see
 * claimMosaicOwnership).
 */
const MOSAIC_OWNER_WINDOW_KEY = 'psmMosaicOwner';

export class Psm extends Core implements IPsm {
  static dependencies: any;

  // Request queue for Doppler privacy API
  requestQueue: Queue;

  // Feature flag client instance
  ffClient: FeatureFlagClient;

  // User's browser -- currently only sets to Safari or Unknown
  browser: string;

  core: any;

  // Navigation tracker for SPA
  navigationTracker?: NavigationTracker;

  // Guards the consent-independent Mosaic-only bootstrap (see initMosaicOnly)
  mosaicReady = false;

  // Guards one-time install of Mosaic DOM/GPT instrumentation (link + slot events)
  mosaicInstrumented = false;

  // True when this instance drives the page's automatic Mosaic emissions
  // (see claimMosaicOwnership)
  mosaicOwner = false;

  // Teardown handles for Mosaic instrumentation listeners
  private mosaicLinkClickHandler?: (event: MouseEvent) => void;
  private mosaicSlotTeardown?: () => void;
  private mosaicSpaTeardown?: () => void;

  constructor() {
    super();
    Psm.dependencies = coreDependencies(this);
    psmConstructorProps(this, Psm.dependencies);
    Psm.dependencies.getFlags = this.getFlags.bind(this);
  }

  async initPsm(config: PsmConfig): Promise<void> {
    const win = window as any;
    const psmInitialized = win.psmInitialized;
    if (this.ready || psmInitialized) return;
    this.browser = setBrowser();
    this.config.browser = this.browser;
    this.config = Object.assign(this.config, config);
    createLogger(this);

    if (typeof config === 'undefined') {
      const err = new Error('[PSM]: Please provide a valid configuration to initPsm');
      throw err;
    }

    const init = async () => {
      // Don't initialize Prism if COPPA toggled on
      if (this.coppaPrivacyMode === COPPAPrivacyModesEnum.ZERO_ID) {
        return;
      }
      win.psmInitialized = true;
      const env = config.psmEnvironment.toUpperCase();
      const core: PayloadCore = payloadCore();
      this.core = core;

      this.requestQueue = new Queue('prismCE', queueOptions, processQueue(URLS.identity[env]));
      let inIframe = false;

      // allowing exception for cnn pages on google amp
      if (window.location !== window.parent.location && config.psmBrandToken !== 'cnnamp') {
        // detecting if Prism is loading from iFrame
        return;
      }

      if (config.psmBrandToken === 'cnnamp') {
        inIframe = true;
      }

      this.wmukid = Psm.dependencies.initWMUKID(this.coppaPrivacyMode, inIframe, env);
      this.cdpid = await Psm.dependencies.initCDPID(env, this.config.platform);

      // Set sendWMSegs cookie
      this.setSendWMSegs(env);

      // Set sendAuthToken2 cookie
      this.setSendAuth2Token(env);

      // resolve auth tokens for US, EMEA, and LATAM users across all supported brands
      if (
        CCPA_LOCATIONS.includes(this.countryCode) ||
        EMEA_LOCATIONS.includes(this.countryCode) ||
        LATAM_LOCATIONS.includes(this.countryCode)
      ) {
        Psm.dependencies.resolveAuthTokens(core, cookie, this);
        // A CNN sign-in writes zion.cnn_uid after the waterfall above has already run
        // without it, which left the visitor on a lower-priority token until their next
        // page view. Re-run once the uid lands so Tier 1 resolves in this one. The other
        // tiers self-throttle on the timestamp cookies the first run just wrote, so in
        // practice only CNN Auth goes out.
        watchCnnUid(() => {
          Psm.dependencies.resolveAuthTokens(core, cookie, this);
        });
      }

      // Publish to ZionMessageBus
      if (window.ZionMessageBus) {
        window.ZionMessageBus.getInstance().publish('id_found', { type: 'wmukid', value: this.wmukid });
      }

      // Set session properties with initial page load parameter
      this.setSessionProperties(true, env);

      // Set isInAuthTokenExperiment cookie
      this.setIsInExperiment('isInAuthTokenExperiment', env);

      hydratePayload(this, core);

      core.trackIdentityRegistration('identity on page start', new Date().toISOString(), (data) => {
        if (this.queryFlag('doppler-identity-onstart')) {
          this.requestQueue.addItem(data);
          this.resetNewSessionFields();
        }

        if (env === 'AUTOMATED_TEST') {
          window.localStorage.setItem('payload-on-start', JSON.stringify(data));
        }
      });
      const trackIdentityOnComplete = () => {
        this.setSessionProperties(false, env);
        hydratePayload(this, core);
        core.trackIdentityRegistration('identity on page complete', new Date().toISOString(), (data) => {
          if (this.queryFlag('doppler-identity-oncomplete')) {
            this.requestQueue.addItem(data);
            if (env === 'AUTOMATED_TEST') {
              window.localStorage.setItem('payload-on-complete', JSON.stringify(data));
            }
            this.resetNewSessionFields();
          }
        });
      };
      // Check if the document is already loaded
      if (document.readyState === 'complete') {
        // Page is already loaded - fire the event immediately
        trackIdentityOnComplete();
      } else {
        // Page is not loaded yet - add event listener
        window.addEventListener('load', trackIdentityOnComplete);
      }
      // @ts-ignore
      document.addEventListener('userConsentChanged', (data: { detail: UserConsentEventDetails }) => {
        debug({
          message: 'userConsentChanged event received',
          methodName: 'initPsm',
          eventType: 'privacy',
          context: { eventDetails: data.detail },
        });
        this.setSessionProperties(false, env);
        hydratePayload(this, core);
        core.trackConsentUpdated('userConsentChanged', data.detail, new Date().toISOString(), (data) => {
          if (this.queryFlag('doppler-consent-update')) {
            debug({
              message: 'Consent Update event registered',
              methodName: 'initPsm',
              eventType: 'privacy',
              context: { payload: data },
            });
            this.requestQueue.addItem(data);
            this.resetNewSessionFields();
          }
        });
      });
      if (this.queryFlag('doppler-pubsub-event')) {
        for (const type in config.topics) {
          config.topics[type].forEach((topic: string) => {
            if (window.PubSub) {
              window.PubSub.subscribe(topic, (...args: any[]) => {
                let data = typeof args[1] === 'object' ? args[1] : args[0];
                data = Object.keys(data).length < 3 && data.video ? data.video : data;
                window.psmPubSubData = window.psmPubSubData || {};
                window.psmPubSubData[type] = window.psmPubSubData[type] || {};
                Object.assign(window.psmPubSubData[type], data);
                hydratePayload(this, core);
                core.trackPubSub(topic, new Date().toISOString(), (data) => {
                  this.requestQueue.addItem(data);
                  this.resetNewSessionFields();
                });
              });
            }
          });
        }
      }

      this.requestQueue.start();

      // Initialize navigation tracker if feature flag is enabled
      if (this.queryFlag('enable-spa-navigation-tracking')) {
        this.navigationTracker = new NavigationTracker({
          psm: this,
          core,
          env,
        });

        this.navigationTracker.startTracking();
        debug({
          message: '[PSM]: Simplified navigation tracker initialized',
          methodName: 'initPsm',
          eventType: 'navigation',
        });
      }

      // Install the Mosaic link/GPT/SPA instrumentation immediately (Phase 6).
      // GPT commonly renders slots before `load` and does not replay events to
      // listeners added later, so subscribing late loses SlotViewable
      // (DAP-114765). No-op unless Mosaic is enabled for this page.
      this.installMosaicInstrumentation();

      // Emit the Mosaic Pageview once document metadata is parsed. Gated
      // downstream: the flag must be on and the page must be editorial.
      this.fireInitialMosaicPageview();

      this.ready = true;
    };

    this.validateConfig(config);

    this.countryCode =
      this.hasProperty('countryCode', config) &&
      typeof config.countryCode !== 'undefined' &&
      this.config.countryCode.length
        ? this.config.countryCode.toUpperCase()
        : '';
    try {
      this.config.appId = typeof this.config.appId != 'undefined' || this.config.appId ? this.config.appId : '';
      const locationV2 = await sendRequest(URLS.locateV2, {
        headers: {
          'app-id': this.config.appId,
        },
      });
      this.location.ip_address = locationV2.ip;
      this.location.country = locationV2.country;
      this.location.country_alpha2 = locationV2.countryAlpha2;
      this.location.country_alpha3 = locationV2.countryAlpha3;
      this.location.continent = locationV2.continent || '';
      this.location.continentName = locationV2.continentName || '';
      this.countryCode = this.countryCode === '' ? this.location.country_alpha2 : this.countryCode;
      this.location.states = [
        {
          cities: [locationV2.city],
          counties: [...locationV2.counties],
          state: locationV2.state,
          zipcodes: [...locationV2.zipcodes],
        },
      ];
      this.location.proxy = locationV2.proxy;
    } catch (err) {
      this.location.country = this.countryCode ?? undefined;
      this.location.country_alpha2 = undefined;
    }

    try {
      // passing in config brand for brand-specific consent logic
      const { shouldLoad, categories } = await checkOutsideUS(config.brand);
      if (CCPA_LOCATIONS.includes(this.countryCode)) {
        this.consentRule = 'US';
      } else if (shouldLoad && LATAM_LOCATIONS.includes(this.countryCode)) {
        this.consentRule = 'LATAM';
      } else if (shouldLoad && EMEA_LOCATIONS.includes(this.countryCode)) {
        this.consentRule = 'GDPR';
      }
      this.consentCategories = categories.reduce((acc, obj) => {
        return {
          ...acc,
          ...obj,
        };
      }, {});
    } catch (err) {
      this.consentCategories = {};
    }

    this.initCoppaPrivacyMode();

    const clientId = `psmFFClient-${this.config.brand}-${this.config.subBrand}`;

    this.ffClient = new FeatureFlagClient({
      configUrl: URLS.featureFlag[config.psmEnvironment.toUpperCase()],
      configRefreshInterval: 60000,
      userTargetingProperties: {
        Platform: ['Web'],
        Brand: [config.brand],
        Browser: this.browser,
        ClientID: clientId,
        CountryCode: this.countryCode,
      },
      clientId: clientId,
    });

    try {
      const flags = await this.ffClient.queryAllFeatureFlags();
      this.flags = flags.results;
      const prismConsented =
        this.consentRule === 'US' ||
        (this.consentRule === 'GDPR' && this.queryFlag('outside-us-location-check')) ||
        (this.consentRule === 'LATAM' && this.queryFlag('outside-us-location-check'));
      if (prismConsented) {
        await init();
      } else {
        // PRISM does not have consent to run (e.g. EMEA without full TCF consent).
        // Mosaic follows the HBO Max / Discovery+ capture-then-apply-downstream
        // consent model, so its events must still be captured. Run a minimal,
        // consent-independent Mosaic-only bootstrap (gated by doppler-mosaic-event)
        // that emits Mosaic events without any of PRISM's own consent-gated tracking.
        await this.initMosaicOnly(config.psmEnvironment.toUpperCase());
      }
    } catch (err) {
      console.log('[PSM]: Error encountered during location check', err);
    }
  }

  /**
   * Minimal, consent-independent bootstrap for Mosaic (Permutive-shaped) events.
   *
   * PRISM's own init is gated on full consent (in EMEA it requires full TCF
   * consent to initialize). Mosaic follows the HBO Max / Discovery+
   * capture-then-apply-downstream model, so its events must still be captured
   * when PRISM does not have consent to run; consent/opt-outs are applied
   * downstream (C360 ID Graph). This path sets up only what a valid Mosaic
   * payload needs — the send queue, the `user_id` waterfall's WMUKID fallback,
   * and the session — and emits Mosaic events WITHOUT any of PRISM's own
   * (consent-gated) event tracking.
   *
   * No-op unless Mosaic is enabled for this page (see `mosaicEnabled`) and
   * disabled entirely under COPPA zero-id.
   */
  async initMosaicOnly(env: string): Promise<void> {
    if (this.mosaicReady || this.ready) return;

    if (!this.mosaicEnabled()) {
      logMosaic(`skipped mosaic-only bootstrap (flag off or non-EMEA geo: ${this.countryCode || 'unknown'})`);
      return;
    }

    // Respect COPPA: no capture in zero-id mode.
    if (this.coppaPrivacyMode === COPPAPrivacyModesEnum.ZERO_ID) {
      return;
    }

    // Mirror PRISM's iframe rule (allow the cnnamp exception).
    let inIframe = false;
    if (window.location !== window.parent.location && this.config.psmBrandToken !== 'cnnamp') {
      return;
    }
    if (this.config.psmBrandToken === 'cnnamp') {
      inIframe = true;
    }

    // Send queue — Mosaic events go through the same receive path as PRISM.
    if (!this.requestQueue) {
      this.requestQueue = new Queue('prismCE', queueOptions, processQueue(URLS.identity[env]));
      this.requestQueue.start();
    }

    // Identifiers + session required for a valid Mosaic payload. The user_id
    // waterfall (Bolt user id > device id > WMUKID) needs WMUKID as its final
    // fallback; session_id comes from the PRISM session.
    this.wmukid = Psm.dependencies.initWMUKID(this.coppaPrivacyMode, inIframe, env);
    this.setSessionProperties(true, env);

    this.mosaicReady = true;
    logMosaic('mosaic-only bootstrap complete (PRISM not consented; capturing Mosaic events)');

    // Install the link/GPT/SPA instrumentation immediately — GPT commonly
    // renders slots before `load` and does not replay events to listeners added
    // later, so subscribing late loses SlotViewable (DAP-114765).
    this.installMosaicInstrumentation();

    // Emit the initial Pageview once document metadata is parsed. Gated
    // downstream.
    this.fireInitialMosaicPageview();
  }

  getWMUKID() {
    return Psm.dependencies.getWMUKID();
  }

  getCDPID() {
    return Psm.dependencies.getCDPID();
  }

  getConsentProperties() {
    return Psm.dependencies.getConsentProperties();
  }

  setSendAuth2Token(env: string) {
    const sendAuth2Token = this.queryFlag('sendAuthToken2');
    cookie.set('sendAuthToken', sendAuth2Token, { secure: env === 'AUTOMATED_TEST' ? false : true });

    return cookie.set('sendAuthToken2', sendAuth2Token, { secure: env === 'AUTOMATED_TEST' ? false : true });
  }

  setIsInExperiment(name: string, env: string) {
    const value = this.queryFlag(name);
    return cookie.set(name, value, { secure: env === 'AUTOMATED_TEST' ? false : true });
  }

  setSendWMSegs(env: string) {
    const sendWMSegs = this.queryFlag('sendWMSegs');
    return cookie.set('sendWMSegs', sendWMSegs, { secure: env === 'AUTOMATED_TEST' ? false : true });
  }

  getPageLoadId(sessionStart, initialPageLoad) {
    const pageLoadIdCookie = cookie.get('psmPageLoadId') as string | null;
    const pageLoadIdInt = pageLoadIdCookie && parseInt(pageLoadIdCookie);
    let pageLoadId = pageLoadIdInt;
    if (sessionStart) {
      pageLoadId = 1;
    }
    if (!sessionStart && initialPageLoad && pageLoadIdInt) {
      pageLoadId = pageLoadIdInt + 1;
    }
    return pageLoadId;
  }

  setSessionProperties(initialPageLoad = false, env: string) {
    const currentTimestamp = new Date();
    let sessionProperties: SessionProperties = {
      isSessionStart: true,
      pageloadid: this.getPageLoadId(true, initialPageLoad),
      psmLastActiveTimestamp: currentTimestamp.toISOString(),
      psmSessionStart: currentTimestamp.toISOString(),
      sessionDuration: 0,
      sessionid: uuid(),
    };

    // Get all stored session info
    const prevSessionId = cookie.get('psmSessionId') as string | null;
    const prevSessionStartCookie = cookie.get('psmSessionStart') as string | null;
    const prevLastActiveTimestampCookie = cookie.get('psmLastActiveTimestamp') as string | null;

    if (prevSessionId !== null) {
      const prevLastActiveTimestamp = new Date(prevLastActiveTimestampCookie);
      const prevSessionStartTimestamp = new Date(prevSessionStartCookie);
      const timeSinceLastActivity = currentTimestamp.getTime() - prevLastActiveTimestamp.getTime(); // time past in miliseconds

      // if session >max time, then create new session
      if (timeSinceLastActivity > MAX_SESSION_DURATION) {
        info({
          message: `[PSM]: Session ${prevSessionId} timed out after ${timeSinceLastActivity / 1000} seconds.`,
          methodName: 'setSessionProperties',
          eventType: 'session',
        });
        sessionProperties = {
          ...sessionProperties,
          previousSession: {
            psmLastActiveTimestamp: prevLastActiveTimestampCookie,
            psmSessionStart: prevSessionStartCookie,
            sessionDuration: (prevLastActiveTimestamp.getTime() - prevSessionStartTimestamp.getTime()) / 1000,
            sessionid: prevSessionId,
          },
        };
        // If session <max time, update psmLastActive timestamp
      } else {
        debug({
          message: `[PSM]: Session ${prevSessionId} still active after ${
            timeSinceLastActivity / 1000
          } seconds. Updating last active timestamp.`,
          methodName: 'setSessionProperties',
          eventType: 'session',
        });
        sessionProperties = {
          isSessionStart: false,
          pageloadid: this.getPageLoadId(false, initialPageLoad),
          psmLastActiveTimestamp: currentTimestamp.toISOString(),
          psmSessionStart: prevSessionStartCookie,
          sessionDuration: (currentTimestamp.getTime() - prevSessionStartTimestamp.getTime()) / 1000,
          sessionid: prevSessionId,
        };
      }
    } else {
      info({
        message: `[PSM]: Creating new session`,
        methodName: 'setSessionProperties',
        eventType: 'session',
      });
    }

    // Set all session cookie values
    cookie.set('psmSessionId', sessionProperties.sessionid, { secure: env === 'AUTOMATED_TEST' ? false : true });
    cookie.set('psmLastActiveTimestamp', sessionProperties.psmLastActiveTimestamp, {
      secure: env === 'AUTOMATED_TEST' ? false : true,
    });
    cookie.set('psmSessionStart', sessionProperties.psmSessionStart, {
      secure: env === 'AUTOMATED_TEST' ? false : true,
    });
    cookie.set('psmPageLoadId', JSON.stringify(sessionProperties.pageloadid), {
      secure: env === 'AUTOMATED_TEST' ? false : true,
    });

    this.session = sessionProperties;
    return;
  }

  // Clears isSessionStart/previousSession properties
  // Called after each queue addition so it's only cleared after sending to Doppler
  resetNewSessionFields() {
    this.session = {
      ...this.session,
      isSessionStart: false,
      previousSession: null,
    };
  }

  getFlags() {
    return this.flags.reduce((flagObj, curr) => ({ ...flagObj, [curr.flagId]: curr }), {});
  }

  getDeviceProperties(): Device {
    return {
      type: Psm.dependencies.platform,
      userAgent: window.navigator.userAgent,
    };
  }

  getLibrary(): Library {
    return {
      name: 'Prism JS',
      version: PSM_SDK_WEB_VERSION,
      initConfig: this.config,
    };
  }

  getIds(): Ids {
    return {
      attuuid: getAttUuid() || '',
      cdpid: Psm.dependencies.getCDPID(),
      cnnuid: getCnnUid() || '',
      convivaid: getConvivaId() || '',
      ecid: getEcid() || '',
      kruxid: getKruxId() || '',
      liverampatsid: getLiveRampAtsId(),
      wminid: cookie.get('wminid') as string,
      zionid: getZionId() || '',
    };
  }

  getNavigationProperties(): NavigationProperties {
    return {
      url: window.location.href,
      rootDomain: findTopDomain(window.location.href),
      referrer: getReferrer(),
      path: window.location.pathname,
      search: window.location.search,
      title: window.document.title,
    };
  }

  /**
   * Resolve the Permutive `user_id` via the Mosaic waterfall (Bolt user id >
   * device id > WMUKID) together with the tier it came from.
   *
   * Mosaic needs the tier alongside the value: the three identifier spaces are
   * indistinguishable by shape, so without it a WMUKID and a Bolt device id are
   * the same opaque string downstream.
   */
  getMosaicIdentity(): ResolvedIdentity {
    return resolveIdentity({
      boltUserId: getBoltUserId(),
      deviceId: getDeviceId(),
      wmukid: this.wmukid as string,
    });
  }

  /** Resolved Permutive `user_id` only. */
  getMosaicUserId(): string {
    return this.getMosaicIdentity().user_id;
  }

  /** Build the common Permutive `client` block for the current page. */
  getMosaicClient(): PermutiveClient {
    return buildClientBlock(this.getNavigationProperties(), window.navigator.userAgent, window.location.hostname);
  }

  /**
   * Build the common Permutive `consent` block from PRISM's resolved consent
   * state (DAP-114771).
   *
   * Mosaic captures events even where PRISM itself may not run and has consent
   * applied downstream in the C360 ID Graph, so the consent state has to ride
   * along on the event for the backend to honor it.
   */
  getMosaicConsent(): PermutiveConsent {
    return buildConsentBlock({ tcf: getTCFSnapshot() });
  }

  /**
   * Build the Permutive `geo_info` block from PRISM's Atlas-resolved location.
   * `isp_info` is intentionally not produced (not required by Mosaic).
   */
  getMosaicGeoInfo(): PermutiveGeoInfo {
    const state = this.location?.states?.[0];
    return buildGeoInfo({
      city: state?.cities?.[0],
      continent: this.location?.continentName || this.location?.continent,
      country: this.location?.country,
      postal_code: state?.zipcodes?.[0],
      province: state?.state,
    });
  }

  /**
   * Rotate the per-pageview `view_id`. Call this on each new pageview so all
   * events fired within that pageview share the same `view_id`.
   */
  newMosaicView(): string {
    return newViewId();
  }

  /**
   * Build a Permutive-shaped Mosaic event envelope from current PRISM state.
   * The common `client` and `consent` blocks are merged into `properties`;
   * callers may override them. `segments` is emitted empty (Mosaic populates it
   * downstream).
   */
  buildMosaicEvent(name: string, properties?: PermutiveEventProperties, ts?: number | string): MosaicEventEnvelope {
    // Resolved once so user_id and identity_type describe the same read of the
    // waterfall, rather than two reads that could straddle a login.
    const identity = this.getMosaicIdentity();
    const event = buildPermutiveEvent({
      name,
      properties: { client: this.getMosaicClient(), consent: this.getMosaicConsent(), ...(properties || {}) },
      identity: {
        id: uuid(),
        identity_type: identity.identity_type,
        session_id: this.session?.sessionid,
        user_id: identity.user_id,
        view_id: getViewId(),
      },
      time: ts,
    });

    const envelope: MosaicEventEnvelope = { eventType: MOSAIC_EVENT_TYPE, ...event };
    logMosaic(`built "${name}" event`, envelope);
    return envelope;
  }

  /**
   * Emit a Permutive-shaped Mosaic event through the existing receive queue.
   * No-op unless the `doppler-mosaic-event` flag is enabled and the queue is
   * initialized. Returns the enqueued envelope (or null when skipped).
   */
  /** Brand-level Mosaic configuration (event toggles + editorial-page gating). */
  getMosaicConfig(): MosaicConfig | undefined {
    return this.config?.psmMosaic;
  }

  /**
   * Whether Mosaic may emit at all on this page.
   *
   * Mosaic replaces Permutive for EMEA audiences only, so emission is restricted
   * to EMEA geos in code rather than left to the feature flag's targeting. An
   * unresolved country is not in `EMEA_LOCATIONS`, so a failed geo lookup fails
   * closed. `countryCode` is resolved before either bootstrap path runs.
   *
   * The local `wmpsm_mosaic` override deliberately bypasses the geo check so the
   * events can be tested from outside EMEA without a VPN. It is opt-in per
   * browser (localStorage/query string) and never set for real traffic.
   */
  private mosaicEnabled(): boolean {
    if (isMosaicTestOverrideEnabled()) {
      return true;
    }
    return this.queryFlag('doppler-mosaic-event') && EMEA_LOCATIONS.includes(this.countryCode);
  }

  trackMosaicEvent(
    name: string,
    properties?: PermutiveEventProperties,
    ts?: number | string,
  ): MosaicEventEnvelope | null {
    if (!this.mosaicEnabled() || !this.requestQueue) {
      logMosaic(`skipped "${name}" (Mosaic not enabled for ${this.countryCode || 'unknown geo'}, or queue not ready)`);
      return null;
    }

    // Editorial-page gating (DAP-114765): fire on all pages under the brand
    // domain except the login page (and any configured excludedPaths).
    if (!isMosaicPageEnabled(this.getMosaicConfig())) {
      logMosaic(`skipped "${name}" (non-editorial page: ${window.location.pathname})`);
      return null;
    }

    const event = this.buildMosaicEvent(name, properties, ts);
    this.requestQueue.addItem(event);
    logMosaic(`queued "${name}" to receive (${URLS.identity[this.config.psmEnvironment?.toUpperCase()]})`);
    return event;
  }

  /**
   * Emit a Permutive-shaped `Pageview` (Mosaic Phase 6) for the current
   * editorial page. Rotates the per-pageview `view_id`, then emits with the
   * `article`, `client`, and `geo_info` blocks (`isp_info` omitted).
   *
   * `article` defaults to document metadata and `geo_info` to PRISM's resolved
   * location; both can be overridden by a brand integration that has richer
   * content/geo data. No-op unless Mosaic is enabled for this page.
   */
  trackMosaicPageview(overrides?: {
    article?: PermutiveArticle;
    geo_info?: PermutiveGeoInfo;
    client?: PermutiveClient;
  }): MosaicEventEnvelope | null {
    const config = this.getMosaicConfig();
    if (!isMosaicEventEnabled(config, 'pageview')) {
      logMosaic('skipped Pageview (pageview disabled in psmMosaic config)');
      return null;
    }
    if (!isMosaicPageEnabled(config)) {
      logMosaic(`skipped Pageview (non-editorial page: ${window.location.pathname})`);
      return null;
    }
    this.newMosaicView();

    // `geo_info` comes from Atlas and `article` from document metadata, so
    // either can be unresolved or throw depending on load timing and page
    // markup. Degrade the payload rather than dropping the Pageview: a partial
    // Pageview is recoverable downstream, a missing one is not.
    let geoInfo = overrides?.geo_info;
    if (!geoInfo) {
      try {
        geoInfo = this.getMosaicGeoInfo();
      } catch (error) {
        logMosaic('geo_info unavailable; emitting Pageview without it', error);
        geoInfo = buildGeoInfo({});
      }
    }

    let article = overrides?.article;
    if (!article) {
      try {
        article = getArticleFromDocument();
      } catch (error) {
        logMosaic('article metadata unavailable; emitting Pageview without it', error);
        article = { description: '', type: '' };
      }
    }

    const properties = buildPageviewProperties({ article, geo_info: geoInfo, client: overrides?.client });
    return this.trackMosaicEvent('Pageview', properties);
  }

  /**
   * Fire the initial Mosaic Pageview as soon as the document's metadata is
   * parsed. The `article` block is read from `<head>` meta tags, which are
   * available at `DOMContentLoaded`; deferring to `load` instead made the
   * Pageview wait on every ad, image and video resource settling, which on
   * editorial pages delayed it by seconds or lost it entirely when a resource
   * stalled (DAP-114765).
   */
  private fireInitialMosaicPageview(): void {
    // Ownership is claimed by installMosaicInstrumentation, which both callers
    // run first. A bundle that lost the claim must not fire its own Pageview.
    if (!this.mosaicOwner) {
      return;
    }
    const fire = () => {
      try {
        this.trackMosaicPageview();
      } catch (error) {
        logMosaic('initial Pageview failed', error);
      }
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fire, { once: true });
    } else {
      fire();
    }
  }

  /**
   * Emit a Permutive-shaped `LinkClick` for a qualifying link. Shares the
   * current pageview's `view_id`. No-op when the LinkClick event is disabled by
   * brand config. `dest_url` is the link destination.
   */
  trackMosaicLinkClick(destUrl: string, overrides?: { client?: PermutiveClient }): MosaicEventEnvelope | null {
    if (!isMosaicEventEnabled(this.getMosaicConfig(), 'linkClick')) {
      return null;
    }
    const properties = buildLinkClickProperties({ dest_url: destUrl, client: overrides?.client });
    return this.trackMosaicEvent('LinkClick', properties);
  }

  /**
   * Emit a Permutive-shaped `SlotViewable` for a GPT ad slot that became
   * viewable. No-op when the SlotViewable event is disabled by brand config.
   */
  trackMosaicSlotViewable(
    attribution: SlotAttribution,
    overrides?: { client?: PermutiveClient },
  ): MosaicEventEnvelope | null {
    if (!isMosaicEventEnabled(this.getMosaicConfig(), 'slotViewable')) {
      return null;
    }
    const properties = buildSlotProperties({ ...attribution, client: overrides?.client });
    return this.trackMosaicEvent(SLOT_VIEWABLE_EVENT, properties);
  }

  /**
   * Emit a Permutive-shaped `SlotClicked` for a GPT ad slot that was clicked.
   * No-op when the SlotClicked event is disabled by brand config.
   */
  trackMosaicSlotClicked(
    attribution: SlotAttribution,
    overrides?: { client?: PermutiveClient },
  ): MosaicEventEnvelope | null {
    if (!isMosaicEventEnabled(this.getMosaicConfig(), 'slotClicked')) {
      return null;
    }
    const properties = buildSlotProperties({ ...attribution, client: overrides?.client });
    return this.trackMosaicEvent(SLOT_CLICKED_EVENT, properties);
  }

  /**
   * Claim page-level ownership of the automatic Mosaic emissions (initial
   * Pageview, SPA pageviews, link and GPT slot listeners).
   *
   * Two PRISM bundles can legitimately share a page — a brand's released build
   * plus a version pinned via `psm_data_test_version`, for example. Each is a
   * separate module instance with its own `view_id`, so without a page-level
   * claim both install their own GPT listeners and every Mosaic event is
   * emitted twice under two different `view_id`s (DAP-114765).
   *
   * `window.psmInitialized` does not cover this. It is only set inside
   * `initPsm`'s consented `init()`, so on an EMEA page — where PRISM has no
   * consent and Mosaic runs through `initMosaicOnly` — it is never armed. The
   * claim is synchronous, so unlike `psmInitialized` (set after several awaits)
   * two concurrently loading bundles cannot both win it.
   *
   * Only the automatic emissions are gated. Explicit `trackMosaic*` calls from
   * brand integration code still work on any instance.
   */
  private claimMosaicOwnership(): boolean {
    if (this.mosaicOwner) {
      return true;
    }
    const win = window as any;
    if (win[MOSAIC_OWNER_WINDOW_KEY]) {
      return false;
    }
    win[MOSAIC_OWNER_WINDOW_KEY] = true;
    this.mosaicOwner = true;
    return true;
  }

  /**
   * Install the Mosaic DOM/GPT instrumentation once: a delegated click listener
   * for LinkClick, and GPT (`googletag`) hooks for SlotViewable/SlotClicked.
   * The listeners are global; per-page and per-event suppression is handled by
   * `trackMosaicEvent`/the track methods, so login pages emit nothing.
   *
   * No-op unless Mosaic is enabled for this page (see `mosaicEnabled`), only
   * runs once per SDK instance, and only for the instance that owns Mosaic on
   * this page.
   */
  installMosaicInstrumentation(): void {
    if (this.mosaicInstrumented) {
      return;
    }
    if (!this.mosaicEnabled()) {
      return;
    }
    if (!this.claimMosaicOwnership()) {
      logMosaic(
        `skipped Mosaic instrumentation: another PRISM bundle on this page already owns Mosaic (this bundle is ${PSM_SDK_WEB_VERSION})`,
      );
      return;
    }
    this.mosaicInstrumented = true;

    // Events fire for the life of the page and carry the consent state the
    // backend honors, so track CMP changes instead of reporting whatever consent
    // looked like during init (DAP-114771).
    watchTCFConsent();

    const config = this.getMosaicConfig();

    if (isMosaicEventEnabled(config, 'linkClick')) {
      this.mosaicLinkClickHandler = (event: MouseEvent) => {
        const destUrl = resolveLinkClickDest(event);
        if (destUrl) {
          this.trackMosaicLinkClick(destUrl);
        }
      };
      // Capture phase so we still see the click if a handler calls stopPropagation.
      document.addEventListener('click', this.mosaicLinkClickHandler, true);
    }

    if (isMosaicEventEnabled(config, 'slotViewable') || isMosaicEventEnabled(config, 'slotClicked')) {
      this.mosaicSlotTeardown = installGptSlotTracking({
        onSlotViewable: (attribution) => this.trackMosaicSlotViewable(attribution),
        onSlotClicked: (attribution) => this.trackMosaicSlotClicked(attribution),
      });
    }

    // Editorial brands are SPAs: emit a Pageview per virtual route change,
    // deduped by origin + pathname (see mosaic/spaPageview).
    if (isMosaicEventEnabled(config, 'pageview')) {
      this.mosaicSpaTeardown = installMosaicSpaPageviews(() => this.trackMosaicPageview());
    }

    logMosaic('installed Mosaic instrumentation (link + slot + SPA pageview)');
  }

  /**
   * Remove the Mosaic instrumentation listeners. Mainly for tests and for hosts
   * that tear the SDK down; the listeners are otherwise page-lifetime.
   */
  destroyMosaicInstrumentation(): void {
    if (this.mosaicLinkClickHandler) {
      document.removeEventListener('click', this.mosaicLinkClickHandler, true);
      this.mosaicLinkClickHandler = undefined;
    }
    if (this.mosaicSlotTeardown) {
      this.mosaicSlotTeardown();
      this.mosaicSlotTeardown = undefined;
    }
    if (this.mosaicSpaTeardown) {
      this.mosaicSpaTeardown();
      this.mosaicSpaTeardown = undefined;
    }
    if (this.mosaicOwner) {
      (window as any)[MOSAIC_OWNER_WINDOW_KEY] = false;
      this.mosaicOwner = false;
      unwatchTCFConsent();
    }
    this.mosaicInstrumented = false;
    logMosaic('removed Mosaic instrumentation');
  }

  queryFlag(flagId: string): boolean {
    let flagEnabled: boolean = featureFlagDefaults[flagId] || false;
    try {
      const { enabled } = this.flags.find((flag) => {
        return flag.flagId === flagId;
      });

      flagEnabled = enabled;
    } catch (err) {
      warn({
        err,
        methodName: 'queryFlag',
        eventType: 'featureFlagging',
      });
    }

    return flagEnabled;
  }
}

export default Psm;
